/**
 * Musteri paneli.
 *
 * Bes sekme, hepsi calisir: Profil, Guvenlik, Cihazlar, Kredi, Ekip.
 * Spec §4.13 — "her buton calisir": burada gorunen her eylem gercek bir uc
 * noktaya baglidir; gostermelik hicbir sey yok.
 */

import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiRequestError } from '@/lib/api';
import { useAuth, useMe } from '@/lib/auth';
import { useTheme } from '@/lib/theme';
import { copy, localizeCatalogName, useI18n } from '@/lib/i18n';
import { BillingProfileFields, emptyBillingProfile, type BillingProfile } from '@/components/BillingProfileFields';
import { useToast } from '@/lib/toast';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingBlock,
  Modal,
  PageHeader,
  Select,
  StatusDot,
  TableWrap,
  Tabs,
} from '@/components/ui';
import {
  CREDIT_REASON_LABEL,
  compareCatalogDisplayOrder,
  formatDateTime,
  formatNumber,
  formatRelative,
} from '@/lib/format';
import type { CreditBalance, DeviceItem, Entitlement, ThemePreference } from '@shared/index';
import './Account.css';

type Tab = 'profil' | 'guvenlik' | 'cihazlar' | 'kredi' | 'ekip';

export default function Account() {
  const [tab, setTab] = useState<Tab>('profil');
  const me = useMe();

  const tabs: Array<{ id: Tab; label: string }> = [
    { id: 'profil', label: 'Profil' },
    { id: 'guvenlik', label: 'Güvenlik' },
    { id: 'cihazlar', label: 'Cihazlar' },
    ...(me.credits.length > 0 ? [{ id: 'kredi' as const, label: 'Kredi' }] : []),
    { id: 'ekip', label: 'Ekip' },
  ];

  return (
    <div className="account-page stack stack--lg">
      <PageHeader title="Ayarlar" description={`${me.account.name} hesabının profil, ekip ve güvenlik tercihleri.`} />
      <div className="account-shell">
        <Tabs tabs={tabs} value={tab} onChange={setTab} />
        <section className="account-shell__content" aria-live="polite">
          {tab === 'profil' && <ProfileTab />}
          {tab === 'guvenlik' && <SecurityTab />}
          {tab === 'cihazlar' && <DevicesTab />}
          {tab === 'kredi' && <CreditsTab />}
          {tab === 'ekip' && <TeamTab />}
        </section>
      </div>
    </div>
  );
}

// ─── Profil ──────────────────────────────────────────────────────────────────

function ProfileTab() {
  const me = useMe();
  const { reload } = useAuth();
  const { preference, setPreference } = useTheme();
  const { language, setLanguage } = useI18n();
  const toast = useToast();

  const [name, setName] = useState(me.user.name);
  const [fields, setFields] = useState<Record<string, string>>({});

  const save = useMutation({
    mutationFn: () => api.patch('/account/profile', { name: name.trim() }),
    onSuccess: async () => {
      await reload();
      setFields({});
      toast.success('Profil güncellendi.');
    },
    onError: (err) => {
      if (err instanceof ApiRequestError) {
        setFields(err.fields ?? {});
        toast.error(err.message);
      } else toast.error('Kaydedilemedi.');
    },
  });

  const entitlements = useQuery({
    queryKey: ['account', 'entitlements'],
    queryFn: () => api.get<{ items: Entitlement[] }>('/account/entitlements'),
  });

  return (
    <div className="stack">
      {/*
        Baslik YOK: aktif sekme zaten "Profil" diyor. Sayfa basligi "Hesap",
        sekme "Profil", kart basligi da "Profil" olunca ayni kelime ekranda
        iki kez tekrarlaniyor ve hicbir sey ayirt etmiyordu.
      */}
      <Card form>
        <form
          className="stack"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <Field label="Ad soyad" required error={fields['name']}>
            {({ id, invalid }) => (
              <Input id={id} value={name} onChange={(e) => setName(e.target.value)} invalid={invalid} />
            )}
          </Field>

          <Field label="E-posta" hint="E-posta değişikliği için bizimle iletişime geç.">
            {({ id }) => <Input id={id} value={me.user.email} disabled />}
          </Field>

          <div className="row" style={{ gap: 'var(--space-3)' }}>
            <Badge tone={me.user.emailVerified ? 'success' : 'warning'}>
              {me.user.emailVerified ? 'E-posta doğrulandı' : 'E-posta doğrulanmadı'}
            </Badge>
            <Badge tone="primary">{roleLabel(me.user.role)}</Badge>
          </div>

          <div>
            <Button type="submit" variant="primary" loading={save.isPending} disabled={name === me.user.name}>
              Kaydet
            </Button>
          </div>
        </form>
      </Card>

      {me.user.role === 'customer_admin' && <AccountBillingProfile />}

      <Card title="Görünüm" form>
        <div className="stack">
          <Field label={language === 'tr' ? 'Dil' : 'Language'} hint={language === 'tr' ? 'Seçimin bu hesapla kullandığın cihazlara taşınır.' : 'Your selection follows this account across devices.'}>
            {({ id }) => (
              <Select id={id} value={language} onChange={(e) => setLanguage(e.target.value as 'tr' | 'en')}>
                <option value="tr">Türkçe</option>
                <option value="en">English</option>
              </Select>
            )}
          </Field>
        <Field label={language === 'tr' ? 'Tema' : 'Theme'} hint={language === 'tr' ? "Seçimin CORTEX'e de taşınır." : 'Your selection also applies to CORTEX.'}>
            {({ id }) => (
              <Select
                id={id}
                value={preference}
                onChange={(e) => setPreference(e.target.value as ThemePreference)}
              >
                <option value="system">{language === 'tr' ? 'Sistemi takip et' : 'Use system setting'}</option>
                <option value="dark">{language === 'tr' ? 'Koyu' : 'Dark'}</option>
                <option value="light">{language === 'tr' ? 'Açık' : 'Light'}</option>
              </Select>
            )}
          </Field>
        </div>
      </Card>

      <Card title="Hesabına tanımlı ürünler">
        {entitlements.isLoading ? (
          <LoadingBlock rows={2} />
        ) : (entitlements.data?.items.length ?? 0) === 0 ? (
          <EmptyState
            title="Tanımlı ürün yok"
            description="Hesabına bir ürün veya paket tanımlandığında burada görünür."
          />
        ) : (
          <TableWrap caption="Yetkiler">
            <thead>
              <tr>
                <th>Ürün</th>
                <th>Kaynak</th>
                <th>Bitiş</th>
              </tr>
            </thead>
            <tbody>
              {entitlements.data!.items.map((e) => (
                <tr key={e.productId}>
                  <td>
                    <strong>{e.productName}</strong>
                  </td>
                  <td className="muted small">
                    {e.viaPackageName ? `${e.viaPackageName} paketi` : 'Doğrudan'}
                  </td>
                  <td>
                    {e.expiresAt ? (
                      <>
                        {formatDateTime(e.expiresAt)}{' '}
                        <span className="muted small">({formatRelative(e.expiresAt)})</span>
                      </>
                    ) : (
                      <Badge tone="success">Süresiz</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Card>
    </div>
  );
}

function AccountBillingProfile() {
  const { language } = useI18n();
  const c = (tr: string, en: string) => copy(language, tr, en);
  const qc = useQueryClient();
  const toast = useToast();
  const [draft, setDraft] = useState<BillingProfile | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const billing = useQuery({
    queryKey: ['account', 'billing-profile'],
    queryFn: () => api.get<{ profile: BillingProfile | null }>('/account/billing-profile'),
  });
  const save = useMutation({
    mutationFn: (value: BillingProfile) => api.put('/account/billing-profile', value),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['account', 'billing-profile'] });
      void qc.invalidateQueries({ queryKey: ['account', 'offer'] });
      setDraft(null);
      setErrors({});
      toast.success(c('Fatura bilgileri kaydedildi.', 'Billing information saved.'));
    },
    onError: (error) => {
      setErrors(error instanceof ApiRequestError ? error.fields ?? {} : {});
      toast.error(error instanceof Error ? error.message : c('Fatura bilgileri kaydedilemedi.', 'Billing information could not be saved.'));
    },
  });
  const profile = billing.data?.profile;
  const beginEdit = () => { setErrors({}); setDraft(profile ?? { ...emptyBillingProfile }); };
  const rows = profile ? [
    [c('Yasal kurum adı', 'Legal organization name'), profile.legalName],
    [c('Ülke', 'Country'), profile.countryCode],
    ...(profile.countryCode === 'TR' ? [[c('Vergi dairesi', 'Tax office'), profile.taxOffice]] : []),
    [profile.countryCode === 'TR' ? c('Vergi / kimlik numarası', 'Tax / identity number') : c('Şirket kayıt numarası', 'Company registration number'), profile.taxOrRegistrationNumber],
    [c('Fatura adresi', 'Billing address'), profile.address],
    [c('Şehir', 'City'), profile.city],
    [c('Posta kodu', 'Postal code'), profile.postalCode],
  ] : [];
  return <Card title={c('Fatura bilgileri', 'Billing information')} className="account-billing">
    <div className="stack">
      <p className="muted">{c('Kurumunuzun fatura bilgilerini yönetin. Buradaki bilgiler teklif ekranındaki bilgilerle ortaktır.', 'Manage your organization’s billing information. These details are shared with the proposal page.')}</p>
      {billing.isLoading ? <LoadingBlock rows={3} label={c('Fatura bilgileri yükleniyor', 'Loading billing information')} /> : billing.isError ? <ErrorState message={c('Fatura bilgileri alınamadı.', 'Billing information could not be loaded.')} onRetry={() => void billing.refetch()} /> : draft ? (
        <form className="stack" onSubmit={event => { event.preventDefault(); if (!save.isPending) { setErrors({}); save.mutate(draft); } }}>
          <fieldset className="account-billing__fields" disabled={save.isPending}><BillingProfileFields value={draft} onChange={setDraft} errors={errors} /></fieldset>
          <div className="row account-billing__actions">
            <Button variant="primary" type="submit" loading={save.isPending}>{c('Fatura bilgilerini kaydet', 'Save billing information')}</Button>
            <Button type="button" disabled={save.isPending} onClick={() => { setDraft(null); setErrors({}); }}>{c('Vazgeç', 'Cancel')}</Button>
          </div>
        </form>
      ) : <>
        {profile ? <dl className="account-billing__summary">{rows.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value || c('Belirtilmemiş', 'Not specified')}</dd></div>)}</dl> : <p>{c('Henüz fatura bilgisi eklenmemiş.', 'No billing information has been added yet.')}</p>}
        <div><Button onClick={beginEdit} variant={profile ? 'secondary' : 'primary'}>{profile ? c('Fatura bilgilerini düzenle', 'Edit billing information') : c('Fatura bilgilerini ekle', 'Add billing information')}</Button></div>
      </>}
    </div>
  </Card>;
}

// ─── Guvenlik ────────────────────────────────────────────────────────────────

function SecurityTab() {
  const toast = useToast();
  const [confirmLogoutAll, setConfirmLogoutAll] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [setup, setSetup] = useState<{ secret: string; uri: string } | null>(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);

  const twoFactor = useQuery({
    queryKey: ['account', 'security', '2fa'],
    queryFn: () => api.get<{ enabled: boolean; enabledAt: string | null; recoveryCodesRemaining: number }>('/account/security/2fa'),
  });
  const startTwoFactor = useMutation({
    mutationFn: () => api.post<{ secret: string; uri: string }>('/account/security/2fa/setup'),
    onSuccess: setSetup,
    onError: (err) => toast.error(err instanceof ApiRequestError ? err.message : 'Kurulum başlatılamadı.'),
  });
  const confirmTwoFactor = useMutation({
    mutationFn: () => api.post<{ recoveryCodes: string[] }>('/account/security/2fa/confirm', { code: twoFactorCode }),
    onSuccess: (result) => {
      setRecoveryCodes(result.recoveryCodes);
      setSetup(null);
      setTwoFactorCode('');
      void twoFactor.refetch();
      toast.success('İki adımlı doğrulama etkinleştirildi.');
    },
    onError: (err) => toast.error(err instanceof ApiRequestError ? err.message : 'Kod doğrulanamadı.'),
  });
  const disableTwoFactor = useMutation({
    mutationFn: () => api.delete('/account/security/2fa', { code: twoFactorCode }),
    onSuccess: () => {
      setTwoFactorCode('');
      setRecoveryCodes([]);
      void twoFactor.refetch();
      toast.success('İki adımlı doğrulama kapatıldı.');
    },
    onError: (err) => toast.error(err instanceof ApiRequestError ? err.message : 'İşlem tamamlanamadı.'),
  });

  const change = useMutation({
    mutationFn: () =>
      api.post('/account/password', { currentPassword: current, newPassword: next }),
    onSuccess: () => {
      setCurrent('');
      setNext('');
      setRepeat('');
      setFields({});
      toast.success('Parolan güncellendi. Diğer tüm oturumların kapatıldı.');
    },
    onError: (err) => {
      if (err instanceof ApiRequestError) {
        setFields(err.fields ?? {});
        toast.error(err.message);
      } else toast.error('Güncellenemedi.');
    },
  });

  const sessions = useQuery({
    queryKey: ['me', 'sessions'],
    queryFn: () =>
      api.get<{
        items: Array<{
          id: string;
          userAgent: string | null;
          ipAddress: string | null;
          createdAt: string;
        }>;
      }>('/me/sessions'),
  });

  const { logout } = useAuth();
  const revokeAll = useMutation({
    mutationFn: () => api.post<{ revoked: number }>('/auth/logout-all'),
    onSuccess: async () => {
      // Bu oturum DA kapandi (sunucu hepsini iptal etti). Kullaniciyi burada
      // birakmak, her istekte 401 alan olu bir ekran demek olurdu.
      toast.success('Tüm oturumlar kapatıldı. Tekrar giriş yapman gerekiyor.');
      await logout();
    },
    onError: () => toast.error('Oturumlar kapatılamadı.'),
  });

  return (
    <div className="stack">
      <Card title="İki adımlı doğrulama" form>
        {twoFactor.isLoading ? <LoadingBlock rows={2} /> : (
          <div className="stack">
            <div className="row row--between">
              <div>
                <strong>{twoFactor.data?.enabled ? 'Etkin' : 'Kapalı'}</strong>
                <p className="muted small" style={{ margin: 'var(--space-1) 0 0' }}>
                  Yönetici hesaplarında parola ele geçirilse bile erişimi doğrulama uygulamasıyla korur.
                </p>
              </div>
              <Badge tone={twoFactor.data?.enabled ? 'success' : 'warning'}>{twoFactor.data?.enabled ? 'Korunuyor' : 'Kurulum gerekli'}</Badge>
            </div>

            {!twoFactor.data?.enabled && !setup && (
              <div><Button variant="primary" onClick={() => startTwoFactor.mutate()} loading={startTwoFactor.isPending}>Kurulumu başlat</Button></div>
            )}

            {setup && (
              <div className="stack">
                <p className="small">Kimlik doğrulama uygulamanda yeni hesap ekleyip aşağıdaki kurulum anahtarını gir.</p>
                <Input value={setup.secret} readOnly aria-label="TOTP kurulum anahtarı" />
                <Field label="Uygulamadaki altı haneli kod" required>
                  {({ id }) => <Input id={id} value={twoFactorCode} onChange={(e) => setTwoFactorCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code" />}
                </Field>
                <div className="row">
                  <Button variant="primary" onClick={() => confirmTwoFactor.mutate()} loading={confirmTwoFactor.isPending} disabled={!/^\d{6}$/.test(twoFactorCode)}>Doğrula ve etkinleştir</Button>
                  <Button variant="ghost" onClick={() => { setSetup(null); setTwoFactorCode(''); }}>Vazgeç</Button>
                </div>
              </div>
            )}

            {twoFactor.data?.enabled && (
              <div className="stack">
                <p className="muted small">Kalan kurtarma kodu: {twoFactor.data.recoveryCodesRemaining}</p>
                <Field label="Kapatmak için doğrulama veya kurtarma kodu">
                  {({ id }) => <Input id={id} value={twoFactorCode} onChange={(e) => setTwoFactorCode(e.target.value)} autoComplete="one-time-code" />}
                </Field>
                <div><Button variant="danger" onClick={() => disableTwoFactor.mutate()} loading={disableTwoFactor.isPending} disabled={twoFactorCode.length < 6}>İki adımlı doğrulamayı kapat</Button></div>
              </div>
            )}

            {recoveryCodes.length > 0 && (
              <div className="stack">
                <strong>Kurtarma kodlarını şimdi güvenli bir yere kaydet</strong>
                <p className="muted small">Bu kodlar yalnızca bir kez gösterilir ve her biri bir defa kullanılabilir.</p>
                <pre style={{ whiteSpace: 'pre-wrap' }}>{recoveryCodes.join('\n')}</pre>
              </div>
            )}
          </div>
        )}
      </Card>
      <Card title="Parola değiştir" form>
        <form
          className="stack"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            if (next !== repeat) {
              setFields({ repeat: 'Parolalar eşleşmiyor.' });
              return;
            }
            change.mutate();
          }}
        >
          <Field label="Mevcut parola" required error={fields['currentPassword']}>
            {({ id, invalid }) => (
              <Input
                id={id}
                type="password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                autoComplete="current-password"
                invalid={invalid}
              />
            )}
          </Field>

          <Field
            label="Yeni parola"
            required
            error={fields['newPassword']}
            hint="En az 10 karakter. Kaydedince diğer oturumların kapanır."
          >
            {({ id, invalid }) => (
              <Input
                id={id}
                type="password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                autoComplete="new-password"
                invalid={invalid}
              />
            )}
          </Field>

          <Field label="Yeni parola (tekrar)" required error={fields['repeat']}>
            {({ id, invalid }) => (
              <Input
                id={id}
                type="password"
                value={repeat}
                onChange={(e) => setRepeat(e.target.value)}
                autoComplete="new-password"
                invalid={invalid}
              />
            )}
          </Field>

          <div>
            <Button
              type="submit"
              variant="primary"
              loading={change.isPending}
              disabled={!current || !next}
            >
              Parolayı güncelle
            </Button>
          </div>
        </form>
      </Card>

      <Card
        title="Aktif oturumlar"
        action={
          <Button
            variant="ghost"
            size="sm"
            loading={revokeAll.isPending}
            onClick={() => setConfirmLogoutAll(true)}
          >
            Tüm oturumları kapat
          </Button>
        }
      >
        {sessions.isLoading ? (
          <LoadingBlock rows={2} />
        ) : sessions.isError ? (
          <ErrorState message="Oturumlar alınamadı." onRetry={() => void sessions.refetch()} />
        ) : (
          <TableWrap caption="Oturumlar">
            <thead>
              <tr>
                <th>Cihaz / tarayıcı</th>
                <th>IP</th>
                <th>Başlangıç</th>
              </tr>
            </thead>
            <tbody>
              {(sessions.data?.items ?? []).map((s) => (
                <tr key={s.id}>
                  {/* "bu cihaz" rozeti YOK: sunucu hangi oturumun istegi
                      yaptigini bildirmiyor. Uydurmak, yanlis satiri
                      isaretleyip kullaniciyi yaniltmak olurdu. */}
                  <td>{s.userAgent ?? 'Bilinmiyor'}</td>
                  <td className="muted small num">{s.ipAddress ?? '—'}</td>
                  <td className="small">{formatDateTime(s.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Card>
      <ConfirmDialog
        open={confirmLogoutAll}
        onClose={() => setConfirmLogoutAll(false)}
        title="Tüm oturumları kapat"
        description="Bu cihaz dahil hesabın açık olduğu tüm tarayıcı ve CORTEX oturumları kapatılacak. Yeniden giriş yapmanız gerekecek."
        confirmLabel="Oturumları kapat"
        danger
        loading={revokeAll.isPending}
        onConfirm={() => revokeAll.mutate()}
      />
    </div>
  );
}

// ─── Cihazlar ────────────────────────────────────────────────────────────────

function DevicesTab() {
  const me = useMe();
  const toast = useToast();
  const qc = useQueryClient();
  const canManage = me.user.role === 'owner' || me.user.role === 'customer_admin';
  const [renaming, setRenaming] = useState<DeviceItem | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [pendingRevoke, setPendingRevoke] = useState<DeviceItem | null>(null);

  const devices = useQuery({
    queryKey: ['account', 'devices'],
    queryFn: () => api.get<{ items: DeviceItem[] }>('/account/devices'),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.delete(`/account/devices/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['account', 'devices'] });
      setPendingRevoke(null);
          toast.success('Cihaz kaldırıldı. O makinede CORTEX artık açılmayacak.');
    },
    onError: (err) =>
      toast.error(err instanceof ApiRequestError ? err.message : 'Cihaz kaldırılamadı.'),
  });

  const rename = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.patch(`/account/devices/${id}`, { name }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['account', 'devices'] });
      setRenaming(null);
      setRenameValue('');
      toast.success('Cihaz adı güncellendi.');
    },
    onError: () => toast.error('Güncellenemedi.'),
  });

  const items = devices.data?.items ?? [];

  return (
    <>
    <Card
      title={`Cihazlar (${items.length}/${me.account.deviceLimit})`}
        action={<span className="small muted">CORTEX kurulu makineler</span>}
    >
      {devices.isLoading ? (
        <LoadingBlock rows={3} />
      ) : devices.isError ? (
        <ErrorState message="Cihazlar alınamadı." onRetry={() => void devices.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState
          title="Kayıtlı cihaz yok"
            description="Bir makineye CORTEX kurup giriş yaptığınızda cihaz burada görünür."
        />
      ) : (
        <TableWrap caption="Cihazlar">
          <thead>
            <tr>
              <th>Ad</th>
              <th>Son görülme</th>
              <th>Kurulu sürümler</th>
              <th aria-label="İşlemler" />
            </tr>
          </thead>
          <tbody>
            {items.map((d) => (
              <tr key={d.id}>
                <td>
                  <strong>{d.name}</strong>
                  {d.current && (
                    <>
                      {' '}
                      <Badge tone="primary">bu cihaz</Badge>
                    </>
                  )}
                </td>
                <td>
                  <StatusDot
                    tone={
                      d.lastSeenAt && Date.now() - new Date(d.lastSeenAt).getTime() < 3_600_000
                        ? 'success'
                        : 'neutral'
                    }
                    label={formatRelative(d.lastSeenAt)}
                  />
                </td>
                <td className="small muted">
                  {Object.entries(d.installedVersions).length === 0
                    ? '—'
                    : Object.entries(d.installedVersions)
                        .map(([k, v]) => `${k} ${v}`)
                        .join(', ')}
                </td>
                <td>
                  {canManage && (
                    <div className="row" style={{ gap: 4, flexWrap: 'nowrap' }}>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setRenaming(d);
                          setRenameValue(d.name);
                        }}
                      >
                        Yeniden adlandır
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setPendingRevoke(d)}
                      >
                        Kaldır
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      )}
    </Card>
    <Modal
      open={renaming !== null}
      onClose={() => setRenaming(null)}
      title="Cihazı yeniden adlandır"
      description="Operasyon listelerinde kolay ayırt edilecek kısa ve kuruma özgü bir ad kullanın."
      size="sm"
      footer={
        <>
          <Button onClick={() => setRenaming(null)}>Vazgeç</Button>
          <Button
            variant="primary"
            loading={rename.isPending}
            disabled={!renameValue.trim() || renameValue.trim() === renaming?.name}
            onClick={() => {
              if (renaming) rename.mutate({ id: renaming.id, name: renameValue.trim() });
            }}
          >
            Adı güncelle
          </Button>
        </>
      }
    >
      <Field label="Cihaz adı" required>
        {({ id }) => <Input id={id} value={renameValue} onChange={(event) => setRenameValue(event.target.value)} />}
      </Field>
    </Modal>
    <ConfirmDialog
      open={pendingRevoke !== null}
      onClose={() => setPendingRevoke(null)}
        title="CORTEX cihazını kaldır"
        description={`“${pendingRevoke?.name ?? ''}” bu hesaptan kaldırılacak. CORTEX o makinede yeniden etkinleştirilmeden açılamayacak.`}
      confirmLabel="Cihazı kaldır"
      danger
      loading={revoke.isPending}
      onConfirm={() => {
        if (pendingRevoke) revoke.mutate(pendingRevoke.id);
      }}
    />
    </>
  );
}

// ─── Kredi ───────────────────────────────────────────────────────────────────

function CreditsTab() {
  const { language } = useI18n();
  const [page, setPage] = useState(1);

  const summary = useQuery({
    queryKey: ['account', 'credits'],
    queryFn: () => api.get<{ summary: CreditBalance[] }>('/account/credits'),
  });

  const ledger = useQuery({
    queryKey: ['account', 'ledger', page],
    queryFn: () =>
      api.get<{
        items: Array<{
          id: string;
          catalog: string;
          delta: number;
          reason: string;
          note: string | null;
          createdAt: string;
        }>;
        total: number;
        pageSize: number;
      }>('/account/credits/ledger', { page, pageSize: 25 }),
  });

  const wallets = [...(summary.data?.summary ?? [])].sort((a, b) =>
    compareCatalogDisplayOrder(a.catalog, b.catalog),
  );
  const totalPages = ledger.data ? Math.ceil(ledger.data.total / ledger.data.pageSize) : 1;

  return (
    <div className="stack">
      <Card title="Kredi durumu">
        {summary.isLoading ? (
          <LoadingBlock rows={2} />
        ) : wallets.length === 0 ? (
          <EmptyState
            title="Kredi tanımlı değil"
            description="Kredili bir paket tanımlandığında bakiyen burada görünür."
          />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 'var(--space-5)' }}>
            {wallets.map((wallet) => (
              <div className="stack stack--sm" key={wallet.catalog}>
                <strong>{localizeCatalogName(wallet.catalog, language)}</strong>
                <div className="row" style={{ gap: 'var(--space-5)' }}>
                  <div><div className="small muted">Kalan</div><strong className="num" style={{ fontSize: 'var(--text-2xl)' }}>{formatNumber(wallet.balance)}</strong></div>
                  <div><div className="small muted">Yüklenen</div><strong className="num" style={{ fontSize: 'var(--text-xl)' }}>{formatNumber(wallet.granted)}</strong></div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title="Hareketler" padded={false}>
        {ledger.isLoading ? (
          <div style={{ padding: 'var(--space-5)' }}>
            <LoadingBlock rows={3} />
          </div>
        ) : (ledger.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="Hareket yok" description="Kredi yüklemesi ve harcamaları burada listelenir." />
        ) : (
          <>
            <TableWrap caption="Kredi hareketleri">
              <thead>
                <tr>
                  <th>Tarih</th>
                  <th>İşlem</th>
                  <th>Katalog</th>
                  <th>Not</th>
                  <th className="table__num">Değişim</th>
                </tr>
              </thead>
              <tbody>
                {ledger.data!.items.map((row) => (
                  <tr key={row.id}>
                    <td className="small">{formatDateTime(row.createdAt)}</td>
                    <td>{CREDIT_REASON_LABEL[row.reason] ?? row.reason}</td>
                    <td>{localizeCatalogName(row.catalog, language)}</td>
                    <td className="muted small">{row.note ?? '—'}</td>
                    <td
                      className="table__num"
                      style={{ color: row.delta > 0 ? 'var(--success)' : 'var(--text)' }}
                    >
                      {row.delta > 0 ? '+' : ''}
                      {row.delta}
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>

            {totalPages > 1 && (
              <div className="row row--between" style={{ padding: 'var(--space-3) var(--space-4)' }}>
                <Button size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                  ← Önceki
                </Button>
                <span className="small muted">
                  {page} / {totalPages}
                </span>
                <Button size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                  Sonraki →
                </Button>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

// ─── Ekip ────────────────────────────────────────────────────────────────────

function TeamTab() {
  const me = useMe();
  const toast = useToast();
  const qc = useQueryClient();
  const canManage = me.user.role === 'owner' || me.user.role === 'customer_admin';
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invite, setInvite] = useState({ email: '', role: 'customer_user' });

  const members = useQuery({
    queryKey: ['account', 'members'],
    queryFn: () =>
      api.get<{
        items: Array<{
          id: string;
          name: string;
          email: string;
          role: string;
          emailVerified: boolean;
          lastLoginAt: string | null;
          isSelf: boolean;
        }>;
      }>('/account/members'),
  });

  const changeRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: string }) =>
      api.patch(`/account/members/${id}/role`, { role }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['account', 'members'] });
      toast.success('Rol güncellendi.');
    },
    onError: (err) =>
      toast.error(err instanceof ApiRequestError ? err.message : 'Rol değiştirilemedi.'),
  });

  const invitations = useQuery({
    queryKey: ['account', 'invitations'],
    queryFn: () => api.get<{ items: Array<{ id: string; email: string; role: string; expiresAt: string; acceptedAt: string | null; revokedAt: string | null }> }>('/account/invitations'),
    enabled: canManage,
  });
  const sendInvite = useMutation({
    mutationFn: () => api.post('/account/invitations', invite),
    onSuccess: () => { setInviteOpen(false); setInvite({ email: '', role: 'customer_user' }); void invitations.refetch(); toast.success('Davet gönderildi.'); },
    onError: (err) => toast.error(err instanceof ApiRequestError ? err.message : 'Davet gönderilemedi.'),
  });

  return (
    <Card title="Ekip" action={<div className="row"><span className="small muted">{members.data?.items.length ?? 0} kişi</span>{canManage && <Button size="sm" variant="primary" onClick={() => setInviteOpen(true)}>Üye davet et</Button>}</div>}>
      {members.isLoading ? (
        <LoadingBlock rows={3} />
      ) : members.isError ? (
        <ErrorState message="Ekip listesi alınamadı." onRetry={() => void members.refetch()} />
      ) : (
        <TableWrap caption="Ekip üyeleri">
          <thead>
            <tr>
              <th>Kişi</th>
              <th>Rol</th>
              <th>Son giriş</th>
            </tr>
          </thead>
          <tbody>
            {(members.data?.items ?? []).map((m) => (
              <tr key={m.id}>
                <td>
                  <strong>{m.name}</strong>
                  {m.isSelf && (
                    <>
                      {' '}
                      <Badge tone="primary">sen</Badge>
                    </>
                  )}
                  <div className="small muted">{m.email}</div>
                  {!m.emailVerified && <Badge tone="warning">doğrulanmadı</Badge>}
                </td>
                <td>
                  {canManage && !m.isSelf && m.role !== 'owner' ? (
                    <Select
                      value={m.role}
                      aria-label={`${m.name} rolü`}
                      onChange={(e) => changeRole.mutate({ id: m.id, role: e.target.value })}
                    >
                      <option value="customer_admin">Yönetici</option>
                      <option value="customer_user">Operatör</option>
                    </Select>
                  ) : (
                    <Badge>{roleLabel(m.role)}</Badge>
                  )}
                </td>
                <td className="small muted">{formatRelative(m.lastLoginAt)}</td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      )}
      {canManage && (invitations.data?.items.some((item) => !item.acceptedAt && !item.revokedAt) ?? false) && <div className="stack"><h3>Bekleyen davetler</h3>{invitations.data!.items.filter((item) => !item.acceptedAt && !item.revokedAt).map((item) => <div className="row row--between" key={item.id}><span><strong>{item.email}</strong><small className="muted">{roleLabel(item.role)} · {formatRelative(item.expiresAt)}</small></span><Badge tone="warning">Bekliyor</Badge></div>)}</div>}
      <Modal open={inviteOpen} onClose={() => setInviteOpen(false)} title="Ekip üyesi davet et" description="Davet bağlantısı yedi gün geçerlidir." footer={<><Button onClick={() => setInviteOpen(false)}>Vazgeç</Button><Button variant="primary" loading={sendInvite.isPending} disabled={!invite.email} onClick={() => sendInvite.mutate()}>Daveti gönder</Button></>}><div className="stack"><Field label="E-posta" required>{({ id }) => <Input id={id} type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} />}</Field><Field label="Rol" required>{({ id }) => <Select id={id} value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value })}><option value="customer_user">Operatör</option><option value="customer_admin">Yönetici</option></Select>}</Field></div></Modal>
    </Card>
  );
}

function roleLabel(role: string): string {
  return role === 'owner' ? 'Wondeon' : role === 'customer_admin' ? 'Yönetici' : 'Operatör';
}
